import React, {useState, useEffect} from "react";
import {Grid} from "@mui/material";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {toastMessage} from "../commonFunctions";
import {axiosFunction} from "../commonFunctions";
import {styled} from "@mui/material/styles";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {ThreeDots} from "react-loader-spinner";

import * as yup from 'yup';
import {ErrorMessage} from "../../utils/ErrorMessage.tsx";
import TextareaField from "../common/inputs/TextareaField.tsx";
import {InputField} from "../common";
import {v2ReactURL} from "../../utils/constant.ts";

export const CallbackFormSchema = yup.object().shape({
    businessName: yup
        .string()
        .required('Business name is required'),

    contactName: yup
        .string()
        .required('Contact name is required'),

    contactNo: yup
        .string()
        .required('Contact number is required')
        .min(7, 'Invalid mobile number')
        .max(15, 'Invalid mobile number')
        .matches(/^[1-9]\d{2,14}$/, 'Invalid mobile number'),
});

const CallbackForm = z.object({
    businessName: z.string().min(1, "Business name is required"),
    contactName: z.string().min(1, "Contact name is required"),
    contactNo: z.string()
        .min(7, "")
        .max(15, "Invalid mobile number")
        .regex(/^[1-9]\d{2,14}$/, "Invalid mobile number"),
    notes: z.string().max(160).optional()
});

export type CallbackFormType = z.infer<typeof CallbackForm>;

const StyledPDark = styled("p")`
    font-size: 1.2rem;
    font-weight: 400;
    color: #333;
    margin: 0;
    padding: 0;
`;

const StyledPLight = styled("p")`
    font-size: 14px;
    font-weight: 300;
    color: #888;
    margin: 0;
    padding: 5px 0 10px 0;
`;

interface SupportRequestModalProps {
    open: boolean;
    onClose: () => void;
}


const BootstrapDialog = styled(Dialog)(({theme}) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
    // Add the following style to increase the width
    "& .MuiDialog-paperWidthSm": {
        maxWidth: "100%", // Adjust the width as needed
    },
}));

const SupportRequestModal: React.FC<SupportRequestModalProps> = ({open, onClose}) => {
    const sessionList = useSelector((state: any) => state.sessionData.sessionScope);
    const [submitted, setSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const submitStyle = {
        background: "#00A4E5",
        color: "#f2f2f2",
        borderRadius: "5px"
    };

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: {errors}
    } = useForm<CallbackFormType>({
        resolver: yupResolver(CallbackFormSchema),
        defaultValues: {
            businessName: sessionList?.branchName || "",
            contactName: sessionList?.name || "",
            contactNo: sessionList?.mobileno || "",
            notes: ""
        },
        mode: "onChange"
    });

    const notes = watch("notes", "");


    useEffect(() => {
        if (open) {
            setSubmitted(false);
            reset({
                businessName: sessionList?.branchName || "",
                contactName: sessionList?.name || "",
                contactNo: sessionList?.mobileno || "",
                notes: ""
            });
        }
    }, [open, reset, sessionList]);

    const onSubmit = async (data: CallbackFormType) => {
        setIsLoading(true);
        let payload = {
            ...data,
            emailId: sessionList.companyEmail,
            adminMobileNo: sessionList.adminMobileno,
        }
        const response = await axiosFunction(`${v2ReactURL}/support/callback-request`, "POST", payload);
        setIsLoading(false);

        const error: any | null = response?.data?.status ? null : {message: response?.data?.message};
        if (error) {
            toastMessage(error.message);
            return;
        }

        toastMessage("Request submitted successfully", "success");
        setSubmitted(true);
    };

    const handleClose = () => {
        reset();
        setSubmitted(false);
        onClose();
    };

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
            maxWidth="lg"
            sx={{
                "& .MuiDialog-paper": {
                    position: "relative",
                    overflow: "visible",
                    maxWidth: "450px",
                    width: "100%",
                    top: "20px",
                },
            }}
        >
            <DialogContent>

                {submitted ? (
                    <div className="text-center">
                        <button
                            type="button"
                            style={{
                                position: "absolute",
                                right: "-20px",
                                top: "-10px",
                                padding: "5px",
                                backgroundColor: "#0094d6",
                                color: "#FFF",
                                borderRadius: "50%",
                                width: "28px",
                                height: "28px",
                                textAlign: "center",
                                opacity: 1,
                                zIndex: 99,
                                border: "none",
                                lineHeight: "10px"
                            }}
                            onClick={handleClose} // no quotes, pass function reference
                        >
                            <i className="fa fa-close" style={{fontSize: '16px'}}></i>
                        </button>
                        <StyledPDark style={{fontSize: "1.5rem", fontWeight: "bold", padding: "10px"}}>Thank You For
                            Your Request!</StyledPDark>
                        <StyledPLight style={{fontSize: "1.2rem", margin: 0, padding: 0}}>Our expert will connect with
                            you shortly.</StyledPLight>
                        <StyledPLight style={{fontSize: "1.2rem"}}>
                            For urgent queries, contact <strong>support@vasyerp.com</strong>
                        </StyledPLight>
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            style={{
                                position: "absolute",
                                right: "-20px",
                                top: "-10px",
                                padding: "5px",
                                backgroundColor: "#0094d6",
                                color: "#FFF",
                                borderRadius: "50%",
                                width: "28px",
                                height: "28px",
                                textAlign: "center",
                                opacity: 1,
                                zIndex: 99,
                                border: "none",
                                lineHeight: "10px"
                            }}
                            onClick={handleClose} // no quotes, pass function reference
                        >
                            <i className="fa fa-close" style={{fontSize: '16px'}}></i>
                        </button>

                        <StyledPDark className="text-center" style={{fontWeight: 'bold'}}>
                            Talk To Our Expert
                        </StyledPDark>
                        <StyledPDark className="text-center">
                            Fill In Your Info - We'll Reach Out Shortly
                        </StyledPDark>
                        <Grid container spacing={2} mt={1}>
                            <Grid item xs={12}>
                                <InputField
                                    labelName="Business Name"
                                    placeholder="Name *"
                                    name="businessName"
                                    id="businessName"
                                    inputType="text"
                                    register={register}
                                    disabled
                                />
                                {ErrorMessage(errors?.businessName?.message)}
                            </Grid>
                            <Grid item xs={12}>
                                <InputField
                                    labelName="Contact Name"
                                    placeholder="Contact Name *"
                                    name="contactName"
                                    id="contactName"
                                    inputType="text"
                                    register={register}
                                />
                                {ErrorMessage(errors?.contactName?.message)}
                            </Grid>
                            <Grid item xs={12}>
                                <InputField
                                    labelName="Contact No"
                                    placeholder="Enter Your Registered Number *"
                                    name="contactNo"
                                    id="contactNo"
                                    inputType="text"
                                    register={register}
                                />
                                {ErrorMessage(errors?.contactNo?.message)}
                            </Grid>
                            <Grid item xs={12}>
                                <TextareaField
                                    name="notes"
                                    labelName="Notes"
                                    id="notes"
                                    placeholder="Notes"
                                    register={register}
                                    maxLength={200}
                                    value={notes}
                                    onChange={(e) => setValue && setValue("notes", e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}

            </DialogContent>

            <DialogContent>
                <div>
                    <Button
                        disabled={isLoading}
                        autoFocus
                        onClick={submitted ? ()=>{
                            handleClose();
                        } : handleSubmit(onSubmit)}
                        style={submitStyle}
                        className="w-100"
                        sx={{
                            "&.Mui-disabled": {
                                backgroundColor: "rgb(108 213 255) !important",
                            },
                        }}
                    >
                        {submitted ? "Okay" : "Submit"}
                    </Button>
                    &ensp;
                </div>
            </DialogContent>

            <DialogActions>

            </DialogActions>
            {isLoading && (
                <div
                    className="p-4 d-flex  justify-content-center align-items-center h-100 w-100"
                    style={{position: "absolute", background: "#fff", opacity: 0.7}}
                >
                    <div
                        className=""
                        style={{display: "flex", flexDirection: "column"}}
                    >
                        <ThreeDots
                            height="45"
                            width="45"
                            radius="9"
                            color="#00a4e5"
                            ariaLabel="three-dots-loading"
                            wrapperStyle={{}}
                            wrapperClass=""
                        />
                        <span style={{margin: "-9px"}}>Uploading</span>
                    </div>
                </div>
            )}
        </BootstrapDialog>
    );
};

export default SupportRequestModal;
